import { size } from "lodash";
import type { SagaIterator, Task } from "redux-saga";
import { call, fork, join, put, select } from "redux-saga/effects";

import {
  fetchGoodsTypes,
  fetchSailings,
  fetchSenderReceiverCountries,
  fetchVehicleTypeOptionsCached,
} from "../../../common/api";
import {
  getGoodsItemsForLivestockType,
  getGoodsItemsWithoutAnimals,
  getRegistrationNumberAlertGoodsItem,
  getTotalWeightFromGoodsItems,
} from "../../../common/goods/goodsUtils";
import type { Customer } from "../../../common/graphql/fragments/gql/Customer";
import type { Route } from "../../../common/graphql/fragments/gql/Route";
import type { Sailing } from "../../../common/graphql/fragments/gql/Sailing";
import type { SenderReceiverCountry } from "../../../common/graphql/fragments/gql/SenderReceiverCountry";
import type { VehicleType } from "../../../common/graphql/fragments/gql/VehicleType";
import { customsClassificationDropdownEmptyOption } from "../../../common/graphql/useCustomsClassificationsOptions";
import { transformCountryOptions } from "../../../common/graphql/useSenderReceiverCountryOptions";
import {
  handleVehicleRegNoRequest,
  type RegistrationNumberAlertStateChange,
} from "../../../common/registrationNumberAlert/sagas/registrationNumberAlertsSaga";
import { getAppliedDimension } from "../../../common/registrationNumberAlert/utils/registrationNumberAlertUtil";
import {
  getFieldsToShow,
  isAnyVisible,
} from "../../../common/utils/bookingFields";
import { shouldClearUkCustomsClassification } from "../../../common/utils/customsClassificationUtil";
import { transformUpdateBookingFormStateToMinimalBooking } from "../../../common/utils/minimalBooking";
import type {
  Animal,
  CustomsClassification,
} from "../../../common/utils/optionTransformers";
import { getNewBookableSailing } from "../../../common/utils/sailingUtils";
import { formatRegistrationNumberString } from "../../../common/utils/stringUtil";
import { ColumnId } from "../../../gql/graphql";
import { getNumberOfDrivers } from "../../create/utils/transformCreateBookingResults";
import {
  type RegistrationNumberAlertFormState,
  TravelledVehicleType,
} from "../registrationNumberAlert";
import {
  setRegistrationNumberAlert,
  setUpdateValues,
  type TriggerFormUpdateSagaAction,
  triggerFormUpdateSaga,
  type UpdateFormUpdateFormStateAction,
  updateFinished,
  updateRegistrationNumberAlert,
  updateStarted,
  updateValues,
} from "../updateFormActions";
import {
  type BookingFormState,
  type BookingFormStateKeys,
  type FormInstanceId,
  getEditedFormState,
  getRegistrationNumberAlert,
  type UpdateGoodsFormModelRecord,
} from "../updateFormReducer";

function registrationNumberAlertForVehicleTypeExists(
  registrationNumberAlert: RegistrationNumberAlertFormState | undefined,
  vehicleType: VehicleType,
) {
  return (
    registrationNumberAlert?.result &&
    registrationNumberAlert.result.vehicleTypeCode === vehicleType.code
  );
}

/**
 * Fetches the countries for sender and receiver countries and sets them in the state.
 *
 * NOTE: It does not clear countries
 */
export function* setCountries(
  id: FormInstanceId,
  state: BookingFormState,
  route: Route | null | undefined,
  override = false,
) {
  const booking: ReturnType<
    typeof transformUpdateBookingFormStateToMinimalBooking
  > = yield call(transformUpdateBookingFormStateToMinimalBooking, state);

  const fields: ReturnType<typeof getFieldsToShow> = yield call(
    getFieldsToShow,
    booking,
    Boolean(state.customer?.isCashCustomer),
  );

  if (
    !isAnyVisible(fields, [
      ColumnId.SenderCountryCode,
      ColumnId.ReceiverCountryCode,
    ])
  ) {
    return;
  }

  const countries: SenderReceiverCountry[] = yield call(
    fetchSenderReceiverCountries,
  );
  const countryOptions = transformCountryOptions(countries, true);

  if (countryOptions.length === 0) {
    return;
  }

  if (
    route &&
    route.arrivalCountryCode !== "" &&
    (!override || !state.receiverCountry)
  ) {
    yield put(
      updateValues(id, {
        receiverCountry: countryOptions.find(
          (country) => country.value === route.arrivalCountryCode,
        )?.data,
      }),
    );
  }

  if (
    route &&
    route.departureCountryCode !== "" &&
    (!override || !state.senderCountry)
  ) {
    yield put(
      updateValues(id, {
        senderCountry: countryOptions.find(
          (country) => country.value === route.departureCountryCode,
        )?.data,
      }),
    );
  }
}

export function* clearImportExportReferences(id: FormInstanceId) {
  yield put(
    updateValues(id, {
      importReference: "",
      exportReference: "",
    }),
  );
}

export function* onChangeVehicleType(
  action: TriggerFormUpdateSagaAction<VehicleType | undefined>,
) {
  const {
    payload: { id, name, value: vehicleType },
  } = action;

  if (name !== "vehicleType" || !vehicleType) {
    return;
  }

  const state: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );
  const registrationNumberAlert: ReturnType<typeof getRegistrationNumberAlert> =
    yield select(getRegistrationNumberAlert, id);

  if (state) {
    yield fork(setCountries, id, state, state.route);

    if (!vehicleType.useAnimals && state.goods) {
      yield put(
        updateValues(id, {
          goods: getGoodsItemsWithoutAnimals(state.goods),
        }),
      );
    }

    if (
      !registrationNumberAlertForVehicleTypeExists(
        registrationNumberAlert,
        vehicleType,
      )
    ) {
      const { defaultWidth, defaultHeight, defaultLength, defaultTradeWeight } =
        vehicleType;
      const defaultNoOfDrivers = getNumberOfDrivers(vehicleType);

      yield joinUpdateValuesAction(id, {
        height: defaultHeight,
        length: defaultLength,
        noOfDrivers: defaultNoOfDrivers,
        tradeWeight: defaultTradeWeight,
        width: defaultWidth,
      });

      yield put(setRegistrationNumberAlert(id, undefined));
    } else {
      if (registrationNumberAlert?.result) {
        const { result } = registrationNumberAlert;
        const appliedHeight = getAppliedDimension(
          result.height,
          vehicleType.defaultHeight,
        );
        const appliedLength = getAppliedDimension(
          result.length,
          vehicleType.defaultLength,
        );
        const defaultNoOfDrivers = getNumberOfDrivers(
          registrationNumberAlert.vehicleType,
        );

        yield put(
          updateValues(id, {
            height: appliedHeight ? result.height : vehicleType.defaultHeight,
            length: appliedLength ? result.length : vehicleType.defaultLength,
            noOfDrivers: defaultNoOfDrivers,
            width:
              registrationNumberAlert.vehicleType?.defaultWidth ??
              vehicleType.defaultWidth,
          }),
        );
        yield put(
          updateRegistrationNumberAlert(id, {
            appliedTravelledVehicle: true,
            appliedHeight,
            appliedLength,
          }),
        );
      }
    }
  }
}

function* setUkCustomsClassification(
  id: FormInstanceId,
  departureDate: string,
  departureTime: string,
  route: Route,
  previousCustomsClassification: CustomsClassification,
) {
  if (
    shouldClearUkCustomsClassification(
      departureDate,
      departureTime,
      route,
      previousCustomsClassification,
    )
  ) {
    yield put(
      updateValues(id, {
        customsClassification: customsClassificationDropdownEmptyOption.data,
      }),
    );
  }
}

export function* onChangeRoute(
  action: TriggerFormUpdateSagaAction<Route | undefined>,
) {
  const {
    payload: { id, name, value: route },
  } = action;

  const state: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );

  if (route && state) {
    yield fork(setCountries, id, state, route, true);
  }

  if (name !== "route" || !route) {
    return;
  }

  if (!state) {
    return;
  }

  if (!route.useLivestockType && state.goods) {
    yield put(
      updateValues(id, {
        goods: getGoodsItemsWithoutAnimals(state.goods),
      }),
    );
  }

  const vehicleTypes: VehicleType[] = yield call(
    fetchVehicleTypeOptionsCached,
    state.customer!.custNo,
    route.code,
  );

  yield fork(setCountries, id, state, state.route);
  yield fork(clearImportExportReferences, id);

  if (state.vehicleType && state.departureDate) {
    const vehicleTypeInState = state.vehicleType;

    const vehicleTypeOnRoute = vehicleTypes.find(
      (v) => v.code === vehicleTypeInState.code,
    );

    if (!vehicleTypeOnRoute) {
      yield joinUpdateValuesAction(id, {
        vehicleType: undefined,
      });
    }

    let sailing: Sailing | null = null;

    if (state.departureDate) {
      const sailings: Awaited<ReturnType<typeof fetchSailings>> = yield call(
        fetchSailings,
        route.code,
        state.departureDate,
      );

      sailing = getNewBookableSailing(sailings, state.sailing);

      if (sailing && state.route && state.customsClassification) {
        yield fork(
          setUkCustomsClassification,
          id,
          state.departureDate,
          sailing.departureTime,
          state.route,
          state.customsClassification,
        );
      }
    }

    yield joinUpdateValuesAction(id, { sailing: sailing ?? undefined });
  }
}

export function* onChangeCustomer(
  action: TriggerFormUpdateSagaAction<Customer | undefined>,
) {
  const {
    payload: { id, name, value: customer },
  } = action;

  if (name !== "customer" || !customer) {
    return;
  }

  const formState: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );

  if (!formState) {
    return;
  }

  yield fork(setCountries, id, formState, formState.route);

  if (formState.route && formState.vehicleType) {
    const vehicleTypes: VehicleType[] = yield call(
      fetchVehicleTypeOptionsCached,
      customer.custNo,
      formState.route.code,
    );

    const vehicleTypeInState = formState.vehicleType;

    const vehicleTypeOnRoute = vehicleTypes.find(
      (v) => v.code === vehicleTypeInState.code,
    );

    if (!vehicleTypeOnRoute) {
      yield joinUpdateValuesAction(id, {
        vehicleType: undefined,
      });
    }
  }
}

export function* onChangeDepartureDate(
  action: TriggerFormUpdateSagaAction<string | undefined>,
) {
  const {
    payload: { id, name, value: departureDate },
  } = action;

  if (name !== "departureDate" || !departureDate) {
    return;
  }

  const state: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );

  if (!state) {
    return;
  }

  let sailing: Sailing | null = null;

  if (state.route) {
    const sailings: Awaited<ReturnType<typeof fetchSailings>> = yield call(
      fetchSailings,
      state.route.code,
      departureDate,
    );

    sailing = getNewBookableSailing(sailings, state.sailing);

    if (sailing && state.customsClassification) {
      yield fork(
        setUkCustomsClassification,
        id,
        sailing.departureDate,
        sailing.departureTime,
        state.route,
        state.customsClassification,
      );
    }
  }

  yield joinUpdateValuesAction(id, { sailing: sailing ?? undefined });
}

export function* onChangeVehicleRegistration(
  action: TriggerFormUpdateSagaAction<string | undefined>,
) {
  const {
    payload: { id, name, value },
  } = action;

  if (value === undefined) {
    return;
  }

  const vehicleRegNo = formatRegistrationNumberString(value);

  const state: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );
  const previousRegistrationNumberAlert: ReturnType<
    typeof getRegistrationNumberAlert
  > = yield select(getRegistrationNumberAlert, id);

  if (!state || !state.route) {
    return;
  }

  const travelledVehicleType =
    name === "vehicleRegNo"
      ? TravelledVehicleType.Vehicle
      : TravelledVehicleType.Trailer;

  const newRegistrationNumberAlertResult: RegistrationNumberAlertStateChange | null =
    yield call(
      handleVehicleRegNoRequest,
      state.customer!.custNo,
      state.route.code,
      vehicleRegNo,
      travelledVehicleType,
      getTotalWeightFromGoodsItems(state.goods),
      state.hazardousGoods,
      state.vehicleType || null,
      state.height,
      state.length,
      state.loadingListMessage,
    );

  if (newRegistrationNumberAlertResult) {
    yield put(
      setRegistrationNumberAlert(
        id,
        transformRegistrationNumberAlert(newRegistrationNumberAlertResult),
      ),
    );
    yield put(
      setUpdateValues(
        id,
        transformRegistrationNumberAlertToBookingFormState(
          newRegistrationNumberAlertResult,
          state,
          id,
        ),
      ),
    );
  } else if (
    previousRegistrationNumberAlert &&
    previousRegistrationNumberAlert.travelledVehicleType ===
      travelledVehicleType
  ) {
    yield put(setRegistrationNumberAlert(id, undefined));
  }
}

const transformRegistrationNumberAlert = (
  alert: RegistrationNumberAlertStateChange,
): RegistrationNumberAlertFormState => ({
  appliedArrivalNotepad: alert.registrationNumberAlert.appliedArrivalNotepad,
  appliedGoods: alert.registrationNumberAlert.appliedGoods,
  appliedHazardousGoods: alert.registrationNumberAlert.appliedHazardousGoods,
  appliedHeight: alert.registrationNumberAlert.appliedHeight,
  appliedLength: alert.registrationNumberAlert.appliedLength,
  appliedLoadingListMessage:
    alert.registrationNumberAlert.appliedLoadingListMessage,
  appliedTravelledVehicle:
    alert.registrationNumberAlert.appliedTravelledVehicle,
  result: alert.registrationNumberAlert.result,
  travelledVehicleType: alert.registrationNumberAlert.travelledVehicleType,
  vehicleType: alert.registrationNumberAlert.vehicleType,
});

const transformRegistrationNumberAlertToBookingFormState = (
  alert: RegistrationNumberAlertStateChange,
  state: BookingFormState,
  id: FormInstanceId,
): Partial<BookingFormState> => {
  return {
    hazardousGoods:
      alert.hazardousGoods !== undefined
        ? alert.hazardousGoods
        : state.hazardousGoods,
    height: alert.height || state.height,
    length: alert.length || state.length,
    loadingListMessage: alert.loadingListMessage || state.loadingListMessage,
    vehicleType: alert.vehicleType || state.vehicleType,
    goods: alert.goodsWeight
      ? getRegistrationNumberAlertGoodsItem(alert.goodsWeight, {
          useGeneralCargo: id.formId === "Grid",
        })
      : state.goods,
    empty:
      alert.registrationNumberAlert.appliedArrivalNotepad && alert.goodsWeight
        ? false
        : state.empty,
  };
};

function* joinUpdateValuesAction(
  id: FormInstanceId,
  values: Partial<BookingFormState>,
) {
  yield put(setUpdateValues(id, values));
  yield fork(handleUpdateFormValues, updateValues(id, values));
}

export function* handleUpdateFormValues({
  payload: { id, values },
}: UpdateFormUpdateFormStateAction): SagaIterator<void> {
  const actions: Task[] = [];

  yield put(updateStarted(id));

  for (const key of Object.keys(values) as BookingFormStateKeys) {
    if (key === "vehicleRegNo" || key === "trailerRegNo") {
      actions.push(
        yield fork(
          onChangeVehicleRegistration,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    } else if (key === "vehicleType") {
      actions.push(
        yield fork(
          onChangeVehicleType,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    } else if (key === "route") {
      actions.push(
        yield fork(onChangeRoute, triggerFormUpdateSaga(id, key, values[key])),
      );
    } else if (key === "customer") {
      actions.push(
        yield fork(
          onChangeCustomer,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    } else if (key === "departureDate") {
      actions.push(
        yield fork(
          onChangeDepartureDate,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    } else if (key === "livestockType") {
      actions.push(
        yield fork(
          onChangeLivestockType,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    } else if (key === "goods") {
      actions.push(
        yield fork(onChangeGoods, triggerFormUpdateSaga(id, key, values[key])),
      );
    } else if (key === "pluginTemperature") {
      actions.push(
        yield fork(
          onChangePluginTemperature,
          triggerFormUpdateSaga(id, key, values[key]),
        ),
      );
    }
  }

  try {
    if (actions.length > 0) {
      yield join([...actions]);
    }
  } finally {
    yield put(updateFinished(id));
  }
}

function* onChangePluginTemperature(
  action: TriggerFormUpdateSagaAction<string | undefined>,
) {
  const {
    payload: { id, name, value },
  } = action;

  if (name !== "pluginTemperature" || value === undefined) {
    return;
  }

  const state: ReturnType<typeof getEditedFormState> = yield select(
    getEditedFormState,
    id,
  );

  if (state) {
    // Enable plug-in when temperature is provided
    yield put(updateValues(id, { noOfPlugins: value.length > 0 ? 1 : 0 }));
  }
}

function* onChangeGoods(
  action: TriggerFormUpdateSagaAction<
    Awaited<UpdateGoodsFormModelRecord | undefined>
  >,
) {
  const {
    payload: { id, name, value: goods },
  } = action;

  if (name !== "goods" || !goods) {
    return;
  }

  if (size(goods) === 0) {
    yield put(
      updateValues(id, {
        senderCountry: null,
        receiverCountry: null,
      }),
    );
  } else {
    const state: ReturnType<typeof getEditedFormState> = yield select(
      getEditedFormState,
      id,
    );

    if (state) {
      yield fork(setCountries, id, state, state.route);
    }
  }
}

function* onChangeLivestockType(
  action: TriggerFormUpdateSagaAction<Animal | undefined | null>,
) {
  const {
    payload: { id, name, value: livestockType },
  } = action;

  if (name !== "livestockType" || !livestockType) {
    return;
  }

  // We cannot add an ANI goods type in the grid, as we the user can only edit the first goods item
  if (id.formId !== "Grid") {
    const state: ReturnType<typeof getEditedFormState> = yield select(
      getEditedFormState,
      id,
    );

    if (state?.goods) {
      const goodsTypes: Awaited<ReturnType<typeof fetchGoodsTypes>> =
        yield call(fetchGoodsTypes);

      const goods = getGoodsItemsForLivestockType(
        goodsTypes,
        state.goods,
        livestockType,
      );

      yield put(
        updateValues(id, {
          goods,
        }),
      );
    }
  }
}
